import dayjs from 'dayjs';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';

export interface UploadHubFiltersState {
  fileTypes: string[];
  uploaders: string[];
  datePreset: string;
  dateFrom: string;
  dateTo: string;
}

export const EMPTY_UPLOAD_HUB_FILTERS: UploadHubFiltersState = {
  fileTypes: [],
  uploaders: [],
  datePreset: '',
  dateFrom: '',
  dateTo: '',
};

export const UPLOAD_DATE_PRESET_LABELS = ['Today', 'Last 7 days', 'Last 30 days', 'Last 12 months'];

const HASH_PART_SEPARATOR = '~';

export const uploadHubFiltersToHash = (filters: UploadHubFiltersState): string => {
  const parts: string[] = [];
  filters.fileTypes.forEach((type) => parts.push(`type:${type.toLowerCase()}`));
  if (filters.datePreset === 'custom' && filters.dateFrom && filters.dateTo) {
    parts.push(`uploaded:custom:${filters.dateFrom}:${filters.dateTo}`);
  } else if (filters.datePreset === 'before' && filters.dateTo) {
    parts.push(`uploaded:before:${filters.dateTo}`);
  } else if (filters.datePreset === 'after' && filters.dateFrom) {
    parts.push(`uploaded:after:${filters.dateFrom}`);
  } else if (filters.datePreset) {
    parts.push(`uploaded:${filters.datePreset}`);
  }
  return parts.join(HASH_PART_SEPARATOR);
};

export const parseUploadHubFiltersFromHash = (hash: string): UploadHubFiltersState => {
  const cleanHash = hash.replace(/^#/, '');
  if (!cleanHash) return EMPTY_UPLOAD_HUB_FILTERS;

  const fileTypes: string[] = [];
  let datePreset = '';
  let dateFrom = '';
  let dateTo = '';

  cleanHash.split(HASH_PART_SEPARATOR).forEach((part) => {
    if (part.startsWith('type:')) {
      fileTypes.push(part.slice('type:'.length).toUpperCase());
    } else if (part.startsWith('uploaded:custom:')) {
      const [from, to] = part.slice('uploaded:custom:'.length).split(':');
      datePreset = 'custom';
      dateFrom = from ?? '';
      dateTo = to ?? '';
    } else if (part.startsWith('uploaded:before:')) {
      datePreset = 'before';
      dateTo = part.slice('uploaded:before:'.length);
    } else if (part.startsWith('uploaded:after:')) {
      datePreset = 'after';
      dateFrom = part.slice('uploaded:after:'.length);
    } else if (part.startsWith('uploaded:')) {
      datePreset = part.slice('uploaded:'.length);
    }
  });

  return { fileTypes, uploaders: [], datePreset, dateFrom, dateTo };
};

const PRESET_RANGE_START: Record<string, () => dayjs.Dayjs> = {
  today: () => dayjs().startOf('day'),
  'last-7-days': () => dayjs().subtract(7, 'day'),
  'last-30-days': () => dayjs().subtract(30, 'day'),
  'last-12-months': () => dayjs().subtract(12, 'month'),
};

const DATE_RANGE_PRESETS = ['before', 'after', 'custom'];

const fileMatchesDateFilter = (createdAtUtc: string, filters: UploadHubFiltersState): boolean => {
  if (!filters.datePreset) return true;
  const createdAt = dayjs(createdAtUtc);

  if (DATE_RANGE_PRESETS.includes(filters.datePreset)) {
    if (filters.dateFrom && createdAt.isBefore(dayjs(filters.dateFrom).startOf('day'))) return false;
    if (filters.dateTo && createdAt.isAfter(dayjs(filters.dateTo).endOf('day'))) return false;
    return true;
  }

  const rangeStart = PRESET_RANGE_START[filters.datePreset]?.();
  return rangeStart ? !createdAt.isBefore(rangeStart) : true;
};

export const fileMatchesUploadHubFilters = (
  file: Pick<UploadHubListItem, 'contentType' | 'createdAtUtc' | 'addedByUserFullName'>,
  filters: UploadHubFiltersState
): boolean => {
  if (
    filters.fileTypes.length > 0 &&
    !filters.fileTypes.includes(getFileBadge({ contentType: file.contentType }).label)
  ) {
    return false;
  }
  if (filters.uploaders.length > 0 && !filters.uploaders.includes(file.addedByUserFullName)) {
    return false;
  }
  return fileMatchesDateFilter(file.createdAtUtc, filters);
};

export const uploadHubActiveFiltersCount = (filters: UploadHubFiltersState): number =>
  (filters.fileTypes.length > 0 ? 1 : 0) +
  (filters.uploaders.length > 0 ? 1 : 0) +
  (filters.datePreset ? 1 : 0);
